import React from "react";
import { Link } from "react-router-dom";

/**
 * VortexWordmark — CARBON LABS brand identity (component filename kept
 * to avoid touching 20+ import sites across the codebase).
 *
 * Composition mirrors the uploaded brand sheet:
 *   [ C-chevron ]   CARBON
 *                  ── LABS ──   (cyan hairlines flanking)
 *
 * Pure SVG + CSS.  Always sharp on Retina, always visible against dark
 * backgrounds, always symmetrical.
 *
 * Props:
 *   size    — "sm" | "md" | "lg" | "xl"
 *   variant — "linked" (wraps in <Link to="/">) | "static"
 *   showIcon — include the C-chevron badge to the left (default true)
 *   tagline — optional monospaced line rendered underneath
 *   className — pass-through for positioning
 */

const SIZES = {
    sm: { wordmark: 18, labs: 8,  flanks: 26, subGap: 3,  icon: 28, gap: 6  },
    md: { wordmark: 26, labs: 10, flanks: 38, subGap: 4,  icon: 40, gap: 10 },
    lg: { wordmark: 42, labs: 14, flanks: 60, subGap: 6,  icon: 64, gap: 14 },
    xl: { wordmark: 68, labs: 20, flanks: 96, subGap: 10, icon: 100, gap: 22 },
};

/* -----------------------------------------------------------------------------
 * CarbonMark — the C-chevron icon distilled from the brand sheet.
 * Metallic silver body with dark carbon-fibre inset, cyan accent bar, and
 * a subtle drop-shadow filter so it lifts off any dark surface.
 * ---------------------------------------------------------------------------*/
export const CarbonMark = ({ size = 40, className = "" }) => {
    const uid = React.useId().replace(/:/g, "");
    return (
        <svg
            viewBox="0 0 120 120"
            width={size}
            height={size}
            className={className}
            aria-hidden="true"
        >
            <defs>
                <linearGradient id={`cm-body-${uid}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%"   stopColor="#F5F7F8" />
                    <stop offset="35%"  stopColor="#C0C0C0" />
                    <stop offset="65%"  stopColor="#6C7276" />
                    <stop offset="100%" stopColor="#282B2B" />
                </linearGradient>
                <linearGradient id={`cm-inner-${uid}`} x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%"   stopColor="#1A1A1A" />
                    <stop offset="100%" stopColor="#0B0D0F" />
                </linearGradient>
                <pattern id={`cm-cfibre-${uid}`} x="0" y="0" width="4" height="4" patternUnits="userSpaceOnUse">
                    <rect width="4" height="4" fill="#0F1112" />
                    <path d="M0 4 L4 0" stroke="#2A2C2E" strokeWidth="0.6" />
                    <path d="M-1 1 L1 -1 M3 5 L5 3" stroke="#1A1C1E" strokeWidth="0.6" />
                </pattern>
                <filter id={`cm-lift-${uid}`} x="-20%" y="-20%" width="140%" height="140%">
                    <feGaussianBlur in="SourceAlpha" stdDeviation="1.2" />
                    <feOffset dx="0" dy="1.5" result="off" />
                    <feComponentTransfer><feFuncA type="linear" slope="0.55" /></feComponentTransfer>
                    <feMerge>
                        <feMergeNode />
                        <feMergeNode in="SourceGraphic" />
                    </feMerge>
                </filter>
            </defs>

            {/* Chevron C — outer silver plate */}
            <path
                d="M22 12 L92 12 L108 32 L108 88 L92 108 L22 108 L38 86 L38 34 Z"
                fill={`url(#cm-body-${uid})`}
                filter={`url(#cm-lift-${uid})`}
            />
            {/* Chevron C — carbon-fibre inner cavity */}
            <path
                d="M40 22 L84 22 L96 36 L96 84 L84 98 L40 98 L52 82 L52 38 Z"
                fill={`url(#cm-cfibre-${uid})`}
            />
            {/* Cyan accent bar — top */}
            <path
                d="M40 22 L84 22 L82 27 L42 27 Z"
                fill="#00E5FF"
                opacity="0.95"
            />
            {/* Cyan accent bar — bottom */}
            <path
                d="M42 93 L82 93 L84 98 L40 98 Z"
                fill="#00E5FF"
                opacity="0.95"
            />
            {/* Sharp silver rim highlight */}
            <path
                d="M22 12 L92 12 L108 32 L108 88 L92 108 L22 108 L38 86 L38 34 Z"
                fill="none"
                stroke="#E8ECEE"
                strokeWidth="1.2"
                strokeLinejoin="round"
                opacity="0.75"
            />
        </svg>
    );
};

/* Tapered cyan hairline flanking LABS */
const Flank = ({ width, side = "left" }) => {
    const uid = React.useId().replace(/:/g, "");
    return (
        <svg width={width} height="3" viewBox={`0 0 ${width} 3`} aria-hidden="true" style={{ display: "block" }}>
            <defs>
                <linearGradient id={uid} x1="0" y1="0" x2={width} y2="0" gradientUnits="userSpaceOnUse">
                    <stop offset="0%"   stopColor="#00E5FF" stopOpacity={side === "left" ? "0" : "0.9"} />
                    <stop offset="30%"  stopColor="#00E5FF" stopOpacity="1" />
                    <stop offset="70%"  stopColor="#00E5FF" stopOpacity="1" />
                    <stop offset="100%" stopColor="#00E5FF" stopOpacity={side === "left" ? "0.9" : "0"} />
                </linearGradient>
            </defs>
            <line x1="0" y1="1.5" x2={width} y2="1.5" stroke={`url(#${uid})`} strokeWidth="1.4" />
        </svg>
    );
};

/* Metallic embossed text-shadow with cyan rim glow */
const EMBOSS_STYLE = {
    textShadow: [
        "0 1px 0 rgba(240, 244, 248, 0.85)",
        "0 -1px 0 rgba(10, 15, 20, 0.85)",
        "0 2px 0 rgba(15, 20, 25, 0.7)",
        "1px 0 0 rgba(15, 20, 25, 0.45)",
        "-1px 0 0 rgba(10, 15, 20, 0.3)",
        "0 6px 14px rgba(0, 0, 0, 0.75)",
        "0 0 26px rgba(0, 229, 255, 0.35)",
    ].join(", "),
};

// Fonts drawn from the Carbon Labs brand system:
//   Chakra Petch  — engineering / motorsport geometry for the wordmark
//   Rajdhani      — narrow, controlled technical face for tagline + pillars
const CARBON_FONT   = "'Chakra Petch', 'Rajdhani', 'Space Grotesk', Impact, sans-serif";
const TECH_FONT     = "'Rajdhani', 'Chakra Petch', 'Space Grotesk', system-ui, sans-serif";

/* Three-pillar row: SCIENCE • DISCIPLINE • RESULTS */
const BrandPillars = ({ fontSize = 10, className = "" }) => (
    <span
        className={`inline-flex items-center whitespace-nowrap ${className}`}
        data-testid="brand-pillars"
        aria-label="Science, Discipline, Results"
    >
        {["Science", "Discipline", "Results"].map((word, i) => (
            <React.Fragment key={word}>
                {i > 0 && (
                    <span
                        aria-hidden="true"
                        className="mx-1.5 sm:mx-2 inline-block w-1 h-1 rounded-full bg-[var(--cyan)]"
                        style={{ boxShadow: "0 0 6px rgba(0, 229, 255, 0.75)" }}
                    />
                )}
                <span
                    style={{
                        fontFamily: TECH_FONT,
                        fontWeight: 600,
                        fontSize,
                        letterSpacing: "0.35em",
                        textTransform: "uppercase",
                        color: "var(--silver-hi)",
                        lineHeight: 1,
                    }}
                >
                    {word}
                </span>
            </React.Fragment>
        ))}
    </span>
);

export const VortexWordmark = ({
    size = "md",
    variant = "linked",
    tagline,
    showIcon = true,
    showPillars = false,
    className = "",
}) => {
    const s = SIZES[size] || SIZES.md;
    const pillarSize = Math.max(8, Math.round((s.labs || 10) * 0.9));
    const taglineSize = Math.max(9, s.labs - 1);

    const inner = (
        <span
            className={`inline-flex items-center leading-none select-none ${className}`}
            style={{ gap: s.gap }}
            data-testid="vortex-wordmark"
        >
            {showIcon && <CarbonMark size={s.icon} />}
            <span className="inline-flex flex-col items-center">
                {/* CARBON — brushed silver with metallic emboss */}
                <span
                    className="text-metal-gold"
                    style={{
                        fontFamily: CARBON_FONT,
                        fontWeight: 700,
                        fontSize: s.wordmark,
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                        lineHeight: 0.95,
                        fontStretch: "condensed",
                        ...EMBOSS_STYLE,
                    }}
                >
                    Carbon
                </span>

                {/* ── L A B S ── cyan-flanked engineered subhead */}
                <span
                    className="inline-flex items-center"
                    style={{ marginTop: s.subGap, gap: s.wordmark * 0.32 }}
                    aria-hidden="true"
                >
                    <Flank width={s.flanks} side="left" />
                    <span
                        style={{
                            fontFamily: CARBON_FONT,
                            fontWeight: 600,
                            fontSize: s.labs,
                            letterSpacing: "0.55em",
                            textTransform: "uppercase",
                            paddingLeft: "0.55em",
                            color: "#00E5FF",
                            textShadow: "0 0 12px rgba(0, 229, 255, 0.55), 0 1px 0 rgba(0,0,0,0.6)",
                        }}
                    >
                        Labs
                    </span>
                    <Flank width={s.flanks} side="right" />
                </span>

                {tagline && (
                    <span
                        className="mt-1.5 uppercase"
                        style={{
                            fontFamily: TECH_FONT,
                            fontWeight: 500,
                            fontSize: taglineSize,
                            letterSpacing: "0.32em",
                            color: "var(--silver-mid)",
                            lineHeight: 1,
                        }}
                    >
                        {tagline}
                    </span>
                )}

                {showPillars && (
                    <BrandPillars
                        fontSize={pillarSize}
                        className={tagline ? "mt-1.5" : "mt-2"}
                    />
                )}
            </span>
        </span>
    );

    if (variant === "linked") {
        return (
            <Link to="/" aria-label="Carbon Labs — home" className="inline-flex items-center group" data-testid="brand-wordmark-link">
                {inner}
            </Link>
        );
    }
    return inner;
};

export default VortexWordmark;
